// Base app module — contains the core zit framework logic without any
// theme-specific dependencies. Theme-specific code (e.g. KT component
// initialisation, page-loading spinners) should be provided by a
// theme's own app.js that requires and extends this one.
const app = {
    hm: null,
    build: 0,
    baseUrl: '',
    pageToken: '',
    lang: 'en',
    controller: '',
    action: '',
    itemId: '',
    dictionary: {},
    aliases: {},
    messages: {},
    icons: {},
    cssClasses: {},
    components: {},
    uiBlockReasons: [],
    initTime: 0,
    appInitPromise: false,

    init: function () {
        this.initTime = Date.now();
        this.uiBlock('init');
        this.initTheme();

        // Wire the app-wide window-visibility listener before components initialise, so
        // even init-time filter events are observed. It is a delegated document listener,
        // so it is a no-op at init (everything is visible) and needs nothing else ready.
        this.initWindowVisibility();

        this.baseUrl = this.hm.helper.readAttribute('base', 'href');

        if (this.baseUrl.substring(this.baseUrl.length - 1) !== '/') {
            this.baseUrl += '/';
        }

        this.hm.session.checkAuthUrl = this.baseUrl + 'checkAuth.php';
        this.hm.session.loggedIn = this.hm.helper.readAttribute('meta[name="zit-logged-in"]', 'content');
        this.hm.session.isEmployee = this.hm.helper.readAttribute('meta[name="zit-is-employee"]', 'content');

        this.build = this.hm.helper.readAttribute('meta[name="zit-build"]', 'content');
        this.pageToken = this.hm.helper.readAttribute('meta[name="zit-page-token"]', 'content');
        this.controller = this.hm.helper.readAttribute('meta[name="zit-controller"]', 'content');
        this.action = this.hm.helper.readAttribute('meta[name="zit-action"]', 'content');
        this.itemId = this.hm.helper.readAttribute('meta[name="zit-item-id"]', 'content');
        this.lang = this.hm.helper.readAttribute('html', 'lang');

        let promisesArr = [];

        if (this.newBuild()) {
            let loadDictionary = this.hm.helper.fetchJson(this.baseUrl + 'config/dictionary.' + this.lang + '.json').then((data) => {
                this.dictionary = data.texts;
                localStorage.setItem('dictionary', JSON.stringify(this.dictionary));
            });

            promisesArr.push(loadDictionary);

            let loadAliases = this.hm.helper.fetchJson(this.baseUrl + 'config/aliases.' + this.lang + '.json').then((data) => {
                this.aliases = data.aliases;
                localStorage.setItem('aliases', JSON.stringify(this.aliases));
            });

            promisesArr.push(loadAliases);

            let loadMessages = this.hm.helper.fetchJson(this.baseUrl + 'config/messages.' + this.lang + '.json', { cache: "default" }).then((data) => {
                this.messages = data.messages;
                localStorage.setItem('messages', JSON.stringify(this.messages));
            });

            promisesArr.push(loadMessages);

            // Load the theme icons map once; cached by the browser for 24h
            let loadThemeVars = this.fetchJson('xhr', 'themeVars', {}, 'GET').then((data) => {
                this.icons = data.icons;
                this.cssClasses = data.cssClasses;

                localStorage.setItem('icons', JSON.stringify(this.icons));
                localStorage.setItem('cssClasses', JSON.stringify(this.cssClasses));
            });

            promisesArr.push(loadThemeVars);
        } else {
            let dictionary = localStorage.getItem('dictionary');

            if (dictionary !== null) {
                this.dictionary = JSON.parse(dictionary);
            }

            let aliases = localStorage.getItem('aliases');

            if (aliases !== null) {
                this.aliases = JSON.parse(aliases);
            }

            let messages = localStorage.getItem('messages');

            if (messages !== null) {
                this.messages = JSON.parse(messages);
            }

            let icons = localStorage.getItem('icons');

            if (icons !== null) {
                this.icons = JSON.parse(icons);
            }

            let cssClasses = localStorage.getItem('cssClasses');

            if (cssClasses !== null) {
                this.cssClasses = JSON.parse(cssClasses);
            }
        }

        this.appInitPromise = Promise.all(promisesArr);

        this.appInitPromise.then(() => {
            this.appInitPromise = new Promise((resolve) => {
                this.initComponents(document);
                resolve();
            });

            return this.appInitPromise;
        }).then(() => {
            this.appInitPromise = new Promise((resolve) => {
                this.initController(document);
                resolve();
            });
            return this.appInitPromise;
        }).then(() => {
            this.appInitPromise = false;
            this.uiUnblock('init');
            this.initialMessages();
            this.hm.session.extendSession();
            this.hm.session.setIntervals();
            this.hm.session.employeeMenu();
            this.hm.log('full load', Date.now() - this.initTime);
        });
    },

    newBuild: function () {
        let cachedBuild = localStorage.getItem('build');

        if (cachedBuild === null || parseInt(cachedBuild) !== this.build) {
            localStorage.setItem('build', this.build);

            return true;
        }

        return false;
    },

    // Called once at the start of init(); override in a theme's app.js to run
    // theme-specific initialisation (e.g. KTComponents.init(), theme-mode setup).
    initTheme: function () { },

    // No-op implementations; override in a theme's app.js to show/hide
    // a theme-specific page-loading indicator.
    uiBlock: function (reason) {
        this.uiBlockReasons.push(reason);
    },

    uiUnblock: function (reason) {
        let index = this.uiBlockReasons.indexOf(reason);
        if (index !== -1) {
            this.uiBlockReasons.splice(index, 1);
        }
    },

    initialMessages: async function () {
        this.hm.msgs.init();

        await this.readMessages();
    },

    readMessages: async function () {
        let response = await this.fetchJson('xhr', 'messages');

        if (typeof response._messages !== typeof undefined) {
            response._messages.forEach((message) => { this.hm.msgs.show(message); });
        }
    },

    fetchJson: async function (controller, action, data, method = 'POST') {
        if (typeof data === typeof undefined) {
            data = {};
        }

        let callConfig = {
            pageToken: this.pageToken,
            outputEngine: 'jsonHtml',
            controller: controller,
            action: action
        };

        data = Object.assign({}, data, callConfig);

        let fetchConfig = {};

        let qs = '';

        if (method === 'POST') {
            fetchConfig = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            }
        } else {
            let qsArr = [];

            for (let property in data) {
                qsArr.push(encodeURIComponent(property) + "=" + encodeURIComponent(data[property]));
            }

            qs = qsArr.join('&');

            fetchConfig = {
                headers: {
                    'Accept': 'application/json',
                }
            }
        }

        let url = this.baseUrl + 'index.php' + (qs !== '' ? '?' + qs : '');

        return this.hm.helper.fetchJson(url, fetchConfig);
    },

    // fetchJson's counterpart for the `html` output engine (see router::outputEngine()):
    // the response is rendered markup, not the jsonHtml envelope. It takes a ready-built
    // url rather than a controller/action pair, because PHP already built it — that is
    // what lets one code path serve both an action-style config (aliases, itemId path
    // segment, queryString, anchor all baked in) and a direct link.
    //
    // internal: outputEngine + pageToken are added (query string for GET, JSON body for
    // POST — vars::initRequest() merges a JSON body into $_REQUEST, but only when the
    // content type is exactly application/json). external: fetched untouched and
    // uncredentialed, so the remote must send CORS headers and the page's CSP
    // connect-src must allow that origin.
    fetchHtml: async function (url, params, method = 'GET', internal = true) {
        if (typeof params === typeof undefined) {
            params = {};
        }

        let fetchConfig = { headers: { 'Accept': 'text/html' } };

        if (!internal) {
            fetchConfig.mode = 'cors';
            fetchConfig.credentials = 'omit';

            return this.hm.helper.fetchText(url, fetchConfig);
        }

        let data = Object.assign({}, params, {
            outputEngine: 'html',
            pageToken: this.pageToken
        });

        if (method === 'POST') {
            fetchConfig.method = 'POST';
            fetchConfig.headers['Content-Type'] = 'application/json';
            fetchConfig.body = JSON.stringify(data);
        } else {
            let qsArr = [];

            for (let property in data) {
                qsArr.push(encodeURIComponent(property) + "=" + encodeURIComponent(data[property]));
            }

            url += (url.indexOf('?') === -1 ? '?' : '&') + qsArr.join('&');
        }

        return this.hm.helper.fetchText(url, fetchConfig);
    },

    initController: function () {
        let controllerPath = document.body.dataset.zitController;
        this.controller = this.hm.newController(controllerPath);
        if (this.controller !== false) {
            this.controller.init();
        }
    },

    initComponents: function (parent) {
        let elements = parent.querySelectorAll('[data-zit-component]');
        let toInit = [];
        let inited = [];

        elements.forEach((element) => {
            let componentId = element.attributes.id.value;

            if (componentId.length === 0) {
                this.hm.warn('component missing id', element);
            }

            if (typeof this.components[componentId] !== typeof undefined) {
                this.hm.warn('component already exists', componentId, this.components[componentId], this.element);
            }

            let componentType = element.dataset.zitComponent;
            let component = this.hm.newComponent(componentType);
            this.components[componentId] = component;
            this.components[componentId].element = element;
            this.components[componentId].id = componentId;
            toInit.push(component);
        });

        toInit.forEach((component) => { component.init(); });
        toInit.forEach((component) => { component.inited(); inited.push(component.id) });

        this.applyBackgroundColors(parent);

        return inited;
    },

    // Apply any element's data-zit-background-color to its style.backgroundColor.
    // A CSP-safe way to set a server-computed color: inline style attributes are
    // blocked by the CSP, so PHP stamps the color onto the attribute and we set it
    // through the CSSOM here. The value may be any CSS color, including a
    // var(--token) reference (it resolves against :root). Runs for the initial
    // document and every XHR-inserted fragment alike, since both pass through
    // initComponents. Generic, so it is not tied to any one component (e.g. comment
    // author avatars set it via theme::generateColors).
    applyBackgroundColors: function (parent) {
        parent.querySelectorAll('[data-zit-background-color]').forEach((element) => {
            let color = element.dataset.zitBackgroundColor;

            if (color) {
                element.style.backgroundColor = color;
            }
        });
    },

    getComponent: function (componentId) {
        if (typeof this.components[componentId] !== typeof undefined) {
            return this.components[componentId];
        }

        return false;
    },

    elementSubComponents: function (element) {
        let subComponents = [];

        for (let componentId in this.components) {
            if (element === this.components[componentId].element || element.contains(this.components[componentId].element)) {
                subComponents.push(componentId);
            }
        }

        return subComponents;
    },

    removeComponents: function (componentIds) {
        componentIds.forEach((componentId) => {
            // A component's removeSubcomponents() may itself deregister components
            // deeper in the list (a nested container drops its own children), so an
            // id can already be gone by the time the loop reaches it.
            if (typeof this.components[componentId] === typeof undefined) {
                return;
            }

            this.components[componentId].removeSubcomponents();
            delete this.components[componentId];
        });
    },

    blockSubmitActions: function (reason) {
        let componentsToBlock = this.componentsWithExtra('submitAction', true);
        componentsToBlock.forEach((component) => {
            component.block(reason);
        });
    },

    releaseSubmitActions: function (reason) {
        let componentsToBlock = this.componentsWithExtra('submitAction', true);
        componentsToBlock.forEach((component) => {
            component.release(reason);
        });
    },

    utext: function (label) {
        if (typeof this.dictionary[label] !== typeof undefined) {
            let string = this.dictionary[label];

            return this.hm.helper.ucfirst(string);
        } else {
            return label;
        }
    },

    text: function (label) {
        if (typeof this.dictionary[label] !== typeof undefined) {
            return this.dictionary[label];
        } else {
            return label;
        }
    },

    formComponents: function (formId) {
        let formComponents = [];

        for (let componentId in this.components) {
            let component = this.components[componentId];

            if (typeof component.formId !== typeof undefined && component.formId === formId) {
                formComponents.push(component);
            }
        }

        return formComponents;
    },

    componentsWithExtra: function (extraAttribute, value) {
        let components = [];

        for (let componentId in this.components) {
            let component = this.components[componentId];

            if (typeof component.extraAttributes[extraAttribute] !== typeof undefined && component.extraAttributes[extraAttribute] === value) {
                components.push(component);
            }
        }

        return components;
    },

    // App-wide "window" visibility. A window is any element tagged data-zit-window="true"
    // (a collection of components — metronic .card, cleanSlate .zit-cs-card, carousel
    // slides, editList/widgetsList item windows). When the dependables filter hides/shows
    // components it dispatches bubbling zitFilterShown / zitFilterHidden on each component;
    // a single delegated listener here re-evaluates the affected windows: a window with no
    // visible component left is hidden (and emits zitWindowHidden); a hidden window that
    // regains a visible component is shown (and emits zitWindowShown).
    initWindowVisibility: function () {
        // Windows awaiting re-evaluation, plus the debounce timer. One filter change calls
        // filterHide on many components synchronously (a burst), so we collect the affected
        // windows and evaluate them once, after the burst.
        this._pendingWindows = new Set();
        this._windowFlushTimer = false;

        let onFilterEvent = (e) => {
            let windowEl = e.target.closest('[data-zit-window="true"]');

            if (windowEl === null) {
                return;
            }

            this._pendingWindows.add(windowEl);

            if (!this._windowFlushTimer) {
                this._windowFlushTimer = setTimeout(() => { this.flushWindowVisibility(); }, 0);
            }
        };

        document.addEventListener('zitFilterShown', onFilterEvent);
        document.addEventListener('zitFilterHidden', onFilterEvent);
    },

    // Re-evaluate every window collected during the burst, then reset for the next one.
    flushWindowVisibility: function () {
        this._windowFlushTimer = false;

        let windows = this._pendingWindows;
        this._pendingWindows = new Set();

        windows.forEach((windowEl) => { this.updateWindowVisibility(windowEl); });
    },

    // Hide a window that has gone empty, or show one that has regained content — but only
    // on an actual state change, so the initial all-visible burst (and repeat events) are
    // no-ops and nothing loops.
    updateWindowVisibility: function (windowEl) {
        let hasVisible = this.windowHasVisibleComponent(windowEl);
        let currentlyHidden = this.hm.helper.isHidden(windowEl);

        if (!hasVisible && !currentlyHidden) {
            console.log('[zit] window hidden — no visible components', windowEl);
            this.hm.helper.showElement(windowEl, false);
            windowEl.dispatchEvent(new Event('zitWindowHidden', { bubbles: true }));
        } else if (hasVisible && currentlyHidden) {
            console.log('[zit] window shown — visible components present', windowEl);
            this.hm.helper.showElement(windowEl, true);
            windowEl.dispatchEvent(new Event('zitWindowShown', { bubbles: true }));
        }
    },

    // True if at least one registered component inside the window is effectively visible.
    // "Effectively visible" is read from the DOM, not a component's isHidden flag (that
    // flag is only set by filterHide, so it misses content hidden any other way, and a
    // filtered-out card's nested components keep isHidden=false). A component counts as
    // visible only when there is no displayNone-class ancestor between its element and the
    // window — a match at/above the window is ignored, so the window's own hidden state
    // does not mask its content (needed to decide whether to re-show it).
    windowHasVisibleComponent: function (windowEl) {
        let displayNoneClass = this.cssClass('displayNone');

        for (let componentId in this.components) {
            let element = this.components[componentId].element;

            if (!element || element === windowEl || !windowEl.contains(element)) {
                continue;
            }

            let hidden = element.closest('.' + displayNoneClass);

            if (!hidden || hidden === windowEl || !windowEl.contains(hidden)) {
                return true;
            }
        }

        return false;
    },

    newComponent: function (componentType, componentId, element) {
        let newComponent = this.hm.newComponent(componentType);

        this.components[componentId] = newComponent;
        this.components[componentId].element = element;
        this.components[componentId].id = componentId;
        this.components[componentId].componentType = componentType;

        newComponent.init();
        newComponent.inited();

        return newComponent;
    },

    confirmCall: function (message, callable) {
        let confirmComponent = this.hm.newComponent('confirm');
        confirmComponent.message = message;
        confirmComponent.consequence = callable;
        confirmComponent.act();
    },

    addComponentsFromRemoteResponse: function (parentElement, response) {
        if (typeof response.windowsConfig.hiddenAttributes !== typeof undefined) {
            for (let componentId in response.windowsConfig.hiddenAttributes.rows[0].components) {
                let componentInnerHTML = response.components[componentId];
                this.addElement(parentElement, componentInnerHTML);
            }
        }

        response.windowsConfig.mainAttributes.rows.forEach((row) => {
            if (typeof row.structure !== typeof undefined && row.structure) {
                // the grid classes come from the theme's cssClassesMap, as in the server-rendered windows: a
                // Bootstrap tier maps them to row / col-N, a theme with a grid of its own to its own classes
                let rowEl = document.createElement('div');
                this.addGridClasses(rowEl, 'row');
                rowEl.classList.add('zit-mb-3');

                for (let componentId in row.components) {
                    let colEl = document.createElement('div');
                    let span = row.components[componentId];

                    // a numeric span maps to col<N>; 'remaining' to the plain col, whose fill the theme decides
                    this.addGridClasses(colEl, isNaN(span) ? 'col' : 'col' + span);

                    this.addElement(colEl, response.components[componentId]);
                    rowEl.appendChild(colEl);
                }

                parentElement.appendChild(rowEl);
            } else {
                for (let componentId in row.components) {
                    this.addElement(parentElement, response.components[componentId]);
                }
            }
        });
    },

    // Adds the classes a grid label (row, col, col0..col12) maps to in the theme's cssClassesMap; one label may map
    // to several classes. A label the theme does not map adds nothing, rather than cssClass()'s warned zit-*
    // fallback: a Bootstrap tier maps no col0, and the span-0 cells of hidden inputs need no class there.
    addGridClasses: function (element, gridLabel) {
        if (typeof this.cssClasses[gridLabel] === typeof undefined) {
            return;
        }

        let gridClasses = this.cssClasses[gridLabel].split(' ').filter((gridClass) => gridClass.length > 0);

        element.classList.add(...gridClasses);
    },

    addElement: function (parentElement, elementHTML, prepend = false) {
        let newElement = this.hm.helper.string2element(elementHTML);
        if (newElement !== null) {
            if (prepend) {
                parentElement.prepend(newElement);
            } else {
                parentElement.appendChild(newElement);
            }
        }
    },

    addActionsFromRemoteResponse: function (parentElement, response, label = 'general') {
        for (let actionId in response.actions[label]) {
            let actionHTML = response.actions[label][actionId];
            let actionEl = this.hm.helper.string2element(actionHTML);
            this.addAction(actionEl, parentElement);
        }
    },

    addAction: function (action, parentElement, preprend = false) {
        if (this.hm.helper.isString(action)) {
            action = this.hm.helper.string2element(action);
        }

        let buttonElement = typeof action.element !== typeof undefined ? action.element : action;

        if (preprend) {
            parentElement.prepend(buttonElement);
        } else {
            parentElement.appendChild(buttonElement);
        }
    },

    externalScriptsFromRemoteResponse: function (response) {
        if (typeof response._externalScripts !== typeof undefined) {
            //this is not a good idea
            let externalScriptsElement = this.hm.helper.string2element(response._externalScripts);
            document.body.appendChild(externalScriptsElement);
        }
    },

    cssClass: function (cssMapLabel) {
        let transposed = '';
        if (cssMapLabel.indexOf(' ') > -1) {
            let cssMapLabels = cssMapLabel.split(' ');

            cssMapLabels.forEach((singleCssMapLabel) => {
                if (singleCssMapLabel.length) {
                    transposed += (transposed.length ? ' ' : '') + this.cssClass(singleCssMapLabel);
                }
            })
        } else {
            if (typeof this.cssClasses[cssMapLabel] !== typeof undefined) {
                transposed = this.cssClasses[cssMapLabel];
            } else {
                transposed = 'zit-' + this.hm.helper.camelToKebab(cssMapLabel);
                // The theme's cssClassesMap is missing this label — warn so the theme dev sees it
                // and fall back to a derived zit-* class (which the theme may or may not actually
                // define in CSS; if it doesn't, the visual effect simply won't apply).
                this.hm.warn('theme cssClasses.' + cssMapLabel + ' is not defined; falling back to ' + transposed);
            }
        }

        return transposed;
    }
}

module.exports = app;
